import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const [hasRedirected, setHasRedirected] = useState(false);
  const [hasCheckedRole, setHasCheckedRole] = useState(false);
  
  const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN || 'dev-auywx6eq8pzjd3b3.us.auth0.com';
  const auth0ClientId = process.env.REACT_APP_AUTH0_CLIENT_ID || '7AO7hAEXg9TFJNwlCDQeuWERptBsvg5V';

  // Check if Auth0 is properly configured
  const isAuth0Configured = auth0Domain && 
    auth0ClientId &&
    auth0Domain !== 'dev-demo.auth0.com' &&
    auth0Domain !== 'test.auth0.com' &&
    auth0ClientId !== 'demo-client-id' &&
    auth0ClientId !== 'test-client-id';

  useEffect(() => {
    // Skip Auth0 redirect if not configured
    if (!isAuth0Configured) {
      return;
    }
    
    // Only redirect once and not if we're on callback or signout routes
    if (!isLoading && !isAuthenticated && !hasRedirected && 
        location.pathname !== '/callback' && location.pathname !== '/signout') {
      setHasRedirected(true);
      
      // Use Auth0 login redirect
      loginWithRedirect({
        appState: { returnTo: location.pathname }
      });
    }
  }, [isAuthenticated, isLoading, location.pathname, hasRedirected, loginWithRedirect, isAuth0Configured]);

  // Check user role after authentication
  useEffect(() => {
    const checkUserRole = async () => {
      // Skip role checking completely if Auth0 not configured
      if (!isAuth0Configured) {
        console.log('🔍 ProtectedRoute: Auth0 not configured, skipping role check');
        setHasCheckedRole(true);
        return;
      }
      
      console.log('🔍 ProtectedRoute: Checking user role...', {
        isAuthenticated,
        hasCheckedRole,
        currentPath: location.pathname,
        isAuth0Configured
      });
      
      // Check role for authenticated users who haven't been checked yet
      // TEMPORARILY DISABLED: Role checking disabled due to CORS issues with API Gateway
      // TODO: Re-enable once API Gateway CORS is fixed
      if (isAuthenticated && !hasCheckedRole) {
        console.log('🔍 ProtectedRoute: Role checking disabled - allowing access');
        // Skip role checking and allow access
        setHasCheckedRole(true);
        // Don't redirect - just allow user to access the app
      }
      
      /* ORIGINAL ROLE CHECKING CODE - DISABLED DUE TO CORS ISSUES
      if (isAuthenticated && !hasCheckedRole) {
        try {
          const token = await getAccessTokenSilently();
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://main.quila.app'}/api/role-selection/profile`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            
            if (!data.user || !data.user.role) {
              // User has no role, redirect to role selection (unless already there)
              if (location.pathname !== '/role-selection') {
                console.log('🔄 Redirecting to role selection - no role found');
                navigate('/role-selection');
              }
            } else {
              // User has a role, handle redirects appropriately
              if (location.pathname === '/role-selection') {
                console.log('🔄 User has role, redirecting from role selection page');
                if (data.user.role === 'counselor' && !data.user.profile_completed) {
                  navigate('/counselor-verification');
                } else {
                  navigate('/');
                }
              }
            }
          } else {
            // API error, redirect to role selection (unless already there)
            if (location.pathname !== '/role-selection') {
              console.log('🔄 Redirecting to role selection - API error');
              navigate('/role-selection');
            }
          }
          
          // Mark as checked to prevent re-checking
          setHasCheckedRole(true);
        } catch (err) {
          console.log('Role check error:', err);
          // Error occurred, redirect to role selection (unless already there)
          if (location.pathname !== '/role-selection') {
            console.log('🔄 Redirecting to role selection - network error');
            navigate('/role-selection');
          }
          setHasCheckedRole(true);
        }
      }
      */
    };

    // Only run role checking if Auth0 is configured
    if (isAuth0Configured) {
      checkUserRole();
    } else {
      // In demo mode, mark as checked immediately
      setHasCheckedRole(true);
    }
  }, [isAuthenticated, hasCheckedRole, location.pathname, getAccessTokenSilently, navigate, isAuth0Configured]);

  // Show loading while checking authentication (skip if Auth0 not configured)
  if (isLoading && isAuth0Configured) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  // Don't render anything if not authenticated (will redirect) - skip if Auth0 not configured
  if (!isAuthenticated && isAuth0Configured) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Redirecting to login...
      </div>
    );
  }

  // Render children if authenticated
  return children;
};

export default ProtectedRoute; 
